import nameModelValidator from 'forms/shared/validators/models/name/name';
import phoneModelValidator from 'forms/shared/validators/models/phone/phone';
import locationAddressValidator from 'forms/shared/validators/models/location/models/address';
import { relationshipOptions } from 'constants/enums/relationshipOptions';
import { DEFAULT_LATEST } from 'constants/dateLimits';
import {
  ValidateJSModel, TelephoneModel, DateModel, BranchCollectionModel, EmailItemModel, LocationModel,
} from 'forms/shared/types';
import { REQUIRED } from 'constants/errors';
import { setToStartOfMonth } from 'helpers/date';
import * as formTypes from 'constants/formTypes';
import { SF852013, SF85 } from 'constants/formTypes';
import { FormDetails } from 'util/singletons';
import { isPVQForm } from 'helpers/form';
import emailModelValidator from 'forms/shared/validators/models/pvqEmail';
import pvqNameModelValidator from 'forms/shared/validators/models/pvqName/name';
import { DeepSafePartial } from 'types/types';
import { relationshipOptionsPvq } from 'constants/enums/people';
import pvqLocationModelValidator from 'forms/shared/validators/models/location/pvqLocation';
import { isSafeUnitedStates } from 'helpers/locations';
import { RelationshipsPeopleItemModel } from '../types';

const isSF852013 = () => {
  const formDetails = FormDetails.getInstance().getData();
  return (
    (formDetails.formType === SF85 || formDetails.formType === SF852013)
    && formDetails.formVersion === '2013-12'
  );
};

const relationshipsPeopleItemValidator: ValidateJSModel<RelationshipsPeopleItemModel> = {
  ContactFrequency: (value, attributes, attributeName, options) => {
    if (isPVQForm(options?.formType)) {
      return { hasValue: true };
    }

    return {};
  },

  ContactFrequencyOther: (value, attributes, attributeName, options) => {
    if (isPVQForm(options?.formType)
      && attributes?.ContactFrequency?.value === 'Other') {
      return { hasValue: true };
    }

    return {};
  },

  Name: (value, attributes, attributeName, options) => {
    if (isPVQForm(options?.formType)) {
      return {
        model: { validator: pvqNameModelValidator },
        presence: true,
      };
    }

    if (options?.requireRelationshipsPeopleName2013 === false) {
      return {
        presence: true,
        model: { validator: nameModelValidator },
      };
    }

    return {};
  },

  Name2013: (value, attributes, attributeName, options) => {
    if (options?.requireRelationshipsPeopleName2013 === true) {
      return {
        hasValue: true,
      };
    }

    return {};
  },

  Dates: (value, attributes, attributeName, options) => ({
    presence: true,
    daterange: {
      earliest: setToStartOfMonth(options?.applicantBirthdate) as DateModel,
      latest: DEFAULT_LATEST,
    },
  }),

  Rank: (value, attributes, attributeName, options) => {
    if (attributes?.RankNotApplicable?.applicable === false
      // TODO: This should probably be requireRelationshipsPeopleRank
      || options?.requireRelationshipsPeopleEmail === false
    ) {
      return {};
    }

    return {
      presence: true,
      hasValue: true,
    };
  },

  RankNotApplicable: {},

  Relationship: (value, attributes, attributeName, options) => {
    if ((options?.requireRelationshipsPeopleRelationship === false)
      || isPVQForm(options?.formType)) {
      return {};
    }

    return {
      presence: true,
      array: {
        validator: {
          presence: true,
          inclusion: relationshipOptions,
        },
        length: { minimum: 1, message: REQUIRED },
      },
    };
  },

  RelationshipOther: (value, attributes, attributeName, options) => {
    if (isPVQForm(options?.formType)) {
      return {};
    }

    if (attributes?.Relationship?.values?.some((i) => i === 'Other')) {
      return {
        presence: true,
        hasValue: true,
      };
    }

    return {};
  },

  CurrentRelationship: (value, attributes, attributeName, options) => {
    if (isPVQForm(options?.formType)) {
      return {
        hasValue: {
          validator: {
            inclusion: relationshipOptionsPvq,
            presence: true,
          },
        }
      };
    }

    return {};
  },

  CurrentRelationshipOtherExplanation: (value, attributes, attributeName, options) => {
    if (!isPVQForm(options?.formType)
      || attributes?.CurrentRelationship?.value !== 'Other') {
      return {};
    }

    return {
      hasValue: true,
    };
  },

  MobileTelephone: (value, attributes, attributeName, options) => {
    if (isSF852013()) {
      return {};
    }

    const requireNumber = !!attributes?.OtherTelephone?.noNumber;
    const mobilePhoneValidator: ValidateJSModel<TelephoneModel> = { ...phoneModelValidator };
    if ([formTypes.SF85P, formTypes.SF85PS].includes(options?.formType || '')) {
      mobilePhoneValidator.timeOfDay = () => ({});
    }
    return {
      presence: true,
      model: { validator: mobilePhoneValidator, requireNumber },
    };
  },

  OtherTelephone: (value, attributes, attributeName, options) => {
    if (isPVQForm(options?.formType)) {
      return {};
    }

    const requireNumber = isSF852013() ? true : !!attributes?.MobileTelephone?.noNumber;

    return {
      presence: true,
      model: { validator: phoneModelValidator, requireNumber },
    };
  },

  EmailNotApplicable: {},

  Email: (value, attributes, attributeName, options) => {
    if (attributes?.EmailNotApplicable?.applicable === false
      || options?.requireRelationshipsPeopleEmail === false
    ) {
      return {};
    }

    return {
      hasValue: { validator: { email: true } },
    };
  },

  Emails: (
    value: DeepSafePartial<BranchCollectionModel<EmailItemModel> | undefined>,
    attributes,
    attributeName,
    options,
  ) => {
    if (isPVQForm(options?.formType)
      && value?.items?.[0]?.Item?.Has?.value !== 'No') {
      return {
        presence: true,
        branchCollection: {
          validator: {
            Email: {
              model: {
                validator: emailModelValidator,
                requireEmailType: false
              },
            },
          },
        },
      };
    }

    return {};
  },

  Address: (value: DeepSafePartial<LocationModel>, attributes, attributeName, options) => {
    if (isPVQForm(options?.formType)) {
      return {
        location: {
          requireIsMilitaryDiplomaticAddress: true,
          requireMilitaryDiplomaticInstallationName: true,
          requireStreet: isSafeUnitedStates(value),
          requireState: isSafeUnitedStates(value),
          validator: pvqLocationModelValidator,
        },
        presence: true,
      };
    }

    return {
      location: { validator: locationAddressValidator },
      presence: true,
    };
  },

  Comments: {},
};

export default relationshipsPeopleItemValidator;
